import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

export type UserRatingDistribution = { rating: number; count: number; percentage: number };
export type UserRatingSummary = {
    eligibleUsers: number;
    totalRatedUsers: number;
    overallResponseRate: number;
    responses: number;
    averageRating: number;
    medianRating: number;
    modeRating: number;
    positiveResponses: number;
    positiveRate: number;
    neutralResponses: number;
    lowResponses: number;
    lowRate: number;
    distribution: UserRatingDistribution[];
};
export type UserRatingDay = {
    day: string;
    responses: number;
    averageRating: number;
    positiveResponses: number;
    neutralResponses: number;
    lowResponses: number;
    ratingCounts: number[];
};
export type UserRatingBreakdown = {
    name: string;
    responses: number;
    averageRating: number;
    positiveResponses: number;
    positiveRate: number;
    lowResponses: number;
    lowRate: number;
};
export type UserRatingRecent = {
    userId: string;
    userName: string;
    email: string;
    rating: number;
    ratingUpdatedAtUtc: string;
    country: string;
    examName: string;
    deviceClass: string;
    clientKind: string;
    operatingSystem: string;
    browserName: string;
    deviceModel: string;
    appVersion: string;
    lowRatingFollowUpStatus: 'notRequired' | 'pending' | 'submitted' | 'skipped';
    feedbackId: string;
    feedbackReasonCode: string;
    feedbackMessage: string;
    feedbackCreatedAtUtc: string;
};
export type UserRatingsData = {
    generatedAtUtc: string;
    fromUtc: string;
    toUtc: string;
    timeZone: string;
    dateSource: string;
    dataQualityNote: string;
    summary: UserRatingSummary;
    daily: UserRatingDay[];
    byCountry: UserRatingBreakdown[];
    byExam: UserRatingBreakdown[];
    byDeviceClass: UserRatingBreakdown[];
    byOperatingSystem: UserRatingBreakdown[];
    byBrowser: UserRatingBreakdown[];
    byDeviceModel: UserRatingBreakdown[];
    recentRatings: UserRatingRecent[];
};

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}
function asNumber(value: unknown) { return typeof value === 'number' && Number.isFinite(value) ? value : 0; }
function asString(value: unknown) { return typeof value === 'string' ? value : ''; }
function asArray(value: unknown) { return Array.isArray(value) ? value : []; }

function normalizeBreakdown(value: unknown): UserRatingBreakdown {
    const row = asRecord(value);
    return {
        name: asString(row.name), responses: asNumber(row.responses), averageRating: asNumber(row.averageRating),
        positiveResponses: asNumber(row.positiveResponses), positiveRate: asNumber(row.positiveRate),
        lowResponses: asNumber(row.lowResponses), lowRate: asNumber(row.lowRate),
    };
}

function normalize(value: unknown): UserRatingsData {
    const record = asRecord(value);
    const summary = asRecord(record.summary);
    return {
        generatedAtUtc: asString(record.generatedAtUtc), fromUtc: asString(record.fromUtc), toUtc: asString(record.toUtc),
        timeZone: asString(record.timeZone), dateSource: asString(record.dateSource), dataQualityNote: asString(record.dataQualityNote),
        summary: {
            eligibleUsers: asNumber(summary.eligibleUsers), totalRatedUsers: asNumber(summary.totalRatedUsers),
            overallResponseRate: asNumber(summary.overallResponseRate), responses: asNumber(summary.responses),
            averageRating: asNumber(summary.averageRating), medianRating: asNumber(summary.medianRating),
            modeRating: asNumber(summary.modeRating), positiveResponses: asNumber(summary.positiveResponses),
            positiveRate: asNumber(summary.positiveRate), neutralResponses: asNumber(summary.neutralResponses),
            lowResponses: asNumber(summary.lowResponses), lowRate: asNumber(summary.lowRate),
            distribution: asArray(summary.distribution).map((item) => {
                const row = asRecord(item);
                return { rating: asNumber(row.rating), count: asNumber(row.count), percentage: asNumber(row.percentage) };
            }),
        },
        daily: asArray(record.daily).map((item) => {
            const row = asRecord(item);
            return {
                day: asString(row.day), responses: asNumber(row.responses), averageRating: asNumber(row.averageRating),
                positiveResponses: asNumber(row.positiveResponses), neutralResponses: asNumber(row.neutralResponses),
                lowResponses: asNumber(row.lowResponses), ratingCounts: asArray(row.ratingCounts).map(asNumber),
            };
        }),
        byCountry: asArray(record.byCountry).map(normalizeBreakdown),
        byExam: asArray(record.byExam).map(normalizeBreakdown),
        byDeviceClass: asArray(record.byDeviceClass).map(normalizeBreakdown),
        byOperatingSystem: asArray(record.byOperatingSystem).map(normalizeBreakdown),
        byBrowser: asArray(record.byBrowser).map(normalizeBreakdown),
        byDeviceModel: asArray(record.byDeviceModel).map(normalizeBreakdown),
        recentRatings: asArray(record.recentRatings).map((item) => {
            const row = asRecord(item);
            return {
                userId: asString(row.userId), userName: asString(row.userName), email: asString(row.email),
                rating: asNumber(row.rating),
                ratingUpdatedAtUtc: asString(row.ratingUpdatedAtUtc) || asString(row.ratedAtUtc),
                country: asString(row.country), examName: asString(row.examName),
                deviceClass: asString(row.deviceClass) || 'unknown', clientKind: asString(row.clientKind) || 'unknown',
                operatingSystem: asString(row.operatingSystem) || 'unknown', browserName: asString(row.browserName) || 'unknown',
                deviceModel: asString(row.deviceModel), appVersion: asString(row.appVersion),
                lowRatingFollowUpStatus: (() => {
                    const status = asString(row.lowRatingFollowUpStatus);
                    return status === 'pending' || status === 'submitted' || status === 'skipped'
                        ? status
                        : 'notRequired';
                })(),
                feedbackId: asString(row.feedbackId),
                feedbackReasonCode: asString(row.feedbackReasonCode),
                feedbackMessage: asString(row.feedbackMessage),
                feedbackCreatedAtUtc: asString(row.feedbackCreatedAtUtc),
            };
        }),
    };
}

function getErrorMessage(payload: unknown, fallback: string) {
    if (typeof payload === 'string' && payload.trim()) return payload;
    const row = asRecord(payload);
    const message = row.errorMessage ?? row.message ?? row.error ?? row.title ?? row.detail;
    return typeof message === 'string' && message.trim() ? message : fallback;
}

export async function getUserRatings(query: { fromUtc: string; toUtc: string; timeZone: string }) {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const configuredBase = String(import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? settings.adminApiBaseUrl ?? 'https://test-api.pteultra.com/api/v1').replace(/\/+$/, '');
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const apiRoot = /\/api\/v[^/]+$/i.test(configuredBase) ? configuredBase : `${configuredBase}/api/v${apiVersion}`;
    const url = new URL(`${apiRoot}/AdminUserRatings`);
    url.searchParams.set('fromUtc', query.fromUtc);
    url.searchParams.set('toUtc', query.toUtc);
    url.searchParams.set('timeZone', query.timeZone);

    const token = String(session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '');
    const headers = new Headers({ Accept: 'application/json' });
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const response = await fetch(url, { headers });
    const contentType = response.headers.get('content-type') ?? '';
    const payload: unknown = contentType.includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));

    const envelope = asRecord(payload);
    if ('data' in envelope) {
        if (envelope.success === false) throw new Error(getErrorMessage(envelope, 'User ratings request failed'));
        return normalize(envelope.data);
    }
    return normalize(payload);
}
